import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';

type ConfigFormData = {
    titleDe: string;
    titleEn: string;
    subtitleDe: string;
    subtitleEn: string;
    emailPlaceholderDe: string;
    emailPlaceholderEn: string;
    sendLinkButtonTextDe: string;
    sendLinkButtonTextEn: string;
    sendingTextDe: string;
    sendingTextEn: string;
    successMessageDe: string;
    successMessageEn: string;
    notFoundErrorDe: string;
    notFoundErrorEn: string;
    genericErrorDe: string;
    genericErrorEn: string;
    backButtonTextDe: string;
    backButtonTextEn: string;
};

const LoginCMS = () => {
    const [formData, setFormData] = useState<ConfigFormData>({
        titleDe: '',
        titleEn: '',
        subtitleDe: '',
        subtitleEn: '',
        emailPlaceholderDe: '',
        emailPlaceholderEn: '',
        sendLinkButtonTextDe: '',
        sendLinkButtonTextEn: '',
        sendingTextDe: '',
        sendingTextEn: '',
        successMessageDe: '',
        successMessageEn: '',
        notFoundErrorDe: '',
        notFoundErrorEn: '',
        genericErrorDe: '',
        genericErrorEn: '',
        backButtonTextDe: '',
        backButtonTextEn: '',
    });
    const [isDirty, setIsDirty] = useState(false);

    const utils = trpc.useUtils();
    const { data: configData, isLoading } = trpc.bookingFunnelCMS.getLoginConfig.useQuery();

    const updateMutation = trpc.bookingFunnelCMS.updateLoginConfig.useMutation({
        onSuccess: () => {
            toast.success('Konfiguration gespeichert');
            setIsDirty(false);
            utils.bookingFunnelCMS.getLoginConfig.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Speichern', { description: error.message });
        },
    });

    useEffect(() => {
        if (configData?.data) {
            const config = configData.data;
            setFormData({
                titleDe: config.titleDe,
                titleEn: config.titleEn,
                subtitleDe: config.subtitleDe,
                subtitleEn: config.subtitleEn,
                emailPlaceholderDe: config.emailPlaceholderDe,
                emailPlaceholderEn: config.emailPlaceholderEn,
                sendLinkButtonTextDe: config.sendLinkButtonTextDe,
                sendLinkButtonTextEn: config.sendLinkButtonTextEn,
                sendingTextDe: config.sendingTextDe,
                sendingTextEn: config.sendingTextEn,
                successMessageDe: config.successMessageDe,
                successMessageEn: config.successMessageEn,
                notFoundErrorDe: config.notFoundErrorDe,
                notFoundErrorEn: config.notFoundErrorEn,
                genericErrorDe: config.genericErrorDe,
                genericErrorEn: config.genericErrorEn,
                backButtonTextDe: config.backButtonTextDe,
                backButtonTextEn: config.backButtonTextEn,
            });
        }
    }, [configData]);

    const handleChange = (field: keyof ConfigFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setIsDirty(true);
    };

    const handleSave = () => {
        updateMutation.mutate(formData);
    };

    if (isLoading) {
        return <Skeleton className='h-96 w-full' />;
    }

    const renderFieldPair = (labelDe: string, labelEn: string, fieldDe: keyof ConfigFormData, fieldEn: keyof ConfigFormData, multiline = false) => (
        <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
                <Label>{labelDe}</Label>
                {multiline ? (
                    <Textarea
                        value={formData[fieldDe]}
                        onChange={(e) => handleChange(fieldDe, e.target.value)}
                        rows={3}
                    />
                ) : (
                    <Input
                        value={formData[fieldDe]}
                        onChange={(e) => handleChange(fieldDe, e.target.value)}
                    />
                )}
            </div>
            <div className='space-y-2'>
                <Label>{labelEn}</Label>
                {multiline ? (
                    <Textarea
                        value={formData[fieldEn]}
                        onChange={(e) => handleChange(fieldEn, e.target.value)}
                        rows={3}
                    />
                ) : (
                    <Input
                        value={formData[fieldEn]}
                        onChange={(e) => handleChange(fieldEn, e.target.value)}
                    />
                )}
            </div>
        </div>
    );

    return (
        <Card>
            <CardHeader>
                <CardTitle>Login CMS</CardTitle>
                <CardDescription>
                    Verwalten Sie die Texte und Beschriftungen für die Login-Seite.
                </CardDescription>
            </CardHeader>
            <CardContent className='space-y-6'>
                <div className='flex justify-end'>
                    <Button onClick={handleSave} disabled={!isDirty || updateMutation.isPending}>
                        {updateMutation.isPending ? (
                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        ) : (
                            <Save className='mr-2 h-4 w-4' />
                        )}
                        Speichern
                    </Button>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Seitentitel</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Titel (DE)', 'Title (EN)', 'titleDe', 'titleEn')}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Beschreibung</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Untertitel (DE)', 'Subtitle (EN)', 'subtitleDe', 'subtitleEn', true)}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>E-Mail Feld</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Platzhalter (DE)', 'Placeholder (EN)', 'emailPlaceholderDe', 'emailPlaceholderEn')}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Buttons</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Link senden (DE)', 'Send Link (EN)', 'sendLinkButtonTextDe', 'sendLinkButtonTextEn')}
                        {renderFieldPair('Wird gesendet (DE)', 'Sending (EN)', 'sendingTextDe', 'sendingTextEn')}
                        {renderFieldPair('Zurück-Button (DE)', 'Back Button (EN)', 'backButtonTextDe', 'backButtonTextEn')}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Erfolgsmeldung</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Erfolgsmeldung (DE)', 'Success Message (EN)', 'successMessageDe', 'successMessageEn', true)}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className='text-lg'>Fehlermeldungen</CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4'>
                        {renderFieldPair('Nicht gefunden (DE)', 'Not Found (EN)', 'notFoundErrorDe', 'notFoundErrorEn', true)}
                        {renderFieldPair('Allgemeiner Fehler (DE)', 'Generic Error (EN)', 'genericErrorDe', 'genericErrorEn', true)}
                    </CardContent>
                </Card>
            </CardContent>
        </Card>
    );
};

export default LoginCMS;
